import { Prompt, PromptFormData, PromptPreview } from '../types/admin'
import { ConversationSummary, ConversationFilters, ConversationsListResponse } from '../types/conversation'
import { ConversationListParams } from '../types/pagination'
import { ConversationDetailsResponse } from '../types/conversationDetail'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7000'

interface ConversationTokenStats {
  totalTokens: number
  inputTokens: number
  outputTokens: number
  totalCost: number
  messageCount: number
  tag: string | null
}

interface ConversationStatsResponse {
  success: boolean
  conversations: ConversationSummary[]
  totalCount: number
  tagCounts: { [tag: string]: number }
  pagination: {
    limit: number
    offset: number
    hasMore: boolean
  }
}

interface TagStatsResponse {
  success: boolean
  tags: string[]
  tagCounts: { [tag: string]: number }
  totalConversations: number
}

export class AdminApiService {
  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}/api/admin/prompts${endpoint}`
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    }

    const response = await fetch(url, config)
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Network error' }))
      throw new Error(error.error || `HTTP ${response.status}`)
    }

    return response.json()
  }

  private async requestTags<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}/api/admin/tags${endpoint}`
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    }

    const response = await fetch(url, config)
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Network error' }))
      throw new Error(error.error || `HTTP ${response.status}`)
    }

    return response.json()
  }

  private async requestConversations<T>(
    params: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}/api/admin/conversations${params}`
    console.log("🔍 API Call:", url)
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    }

    const response = await fetch(url, config)
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Network error' }))
      console.error("❌ API Error:", error)
      throw new Error(error.error || `HTTP ${response.status}`)
    }

    const result = await response.json()
    console.log("✅ API Response:", { 
      success: result.success, 
      conversationsCount: result.conversations?.length 
    })
    return result
  }

  private async requestStats<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}/api/admin/stats${endpoint}`
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    }

    const response = await fetch(url, config)
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Network error' }))
      throw new Error(error.error || `HTTP ${response.status}`)
    }

    return response.json()
  }

  async getPrompts(): Promise<Prompt[]> {
    return this.request<Prompt[]>('')
  }

  async getPrompt(id: number): Promise<Prompt> {
    return this.request<Prompt>(`/${id}`)
  }

  async createPrompt(data: PromptFormData): Promise<{ id: number; message: string }> {
    return this.request('', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updatePrompt(id: number, data: Partial<PromptFormData>): Promise<{ message: string }> {
    return this.request(`/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deletePrompt(id: number): Promise<{ message: string }> {
    return this.request(`/${id}`, {
      method: 'DELETE',
    })
  }

  async previewPrompt(
    promptText: string,
    variables: Record<string, string>
  ): Promise<PromptPreview> {
    return this.request('/preview', {
      method: 'POST',
      body: JSON.stringify({
        prompt_text: promptText,
        variables,
      }),
    })
  }

  async duplicatePrompt(id: number, newName?: string): Promise<{ id: number; message: string }> {
    return this.request(`/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ new_name: newName }),
    })
  }

  async getAvailableVariables(): Promise<{ variables: string[] }> {
    return this.request('/meta/variables')
  }

  async getCurrentClientTag(phone: string): Promise<{ success: boolean; tag: string | null; name: string | null }> {
    return this.requestTags(`/current/${encodeURIComponent(phone)}`)
  }

  async getAllClientsWithTags(): Promise<any> {
    return this.requestTags('/clients')
  }

  async getAvailableTags(): Promise<{ success: boolean; tags: string[] }> {
    try {
      const response = await this.requestConversations<TagStatsResponse>('/tags')
      return {
        success: response.success,
        tags: response.tags
      }
    } catch (error) {
      console.warn('New conversations/tags endpoint not available, falling back to legacy')
      return this.requestTags('/available-tags')
    }
  }

  async getConversations(params: ConversationListParams): Promise<ConversationsListResponse> {
    try {
      const offset = (params.page - 1) * params.limit
      
      const queryParams = new URLSearchParams({
        limit: params.limit.toString(),
        offset: offset.toString(),
      })

      if (params.tag && params.tag !== 'all') {
        queryParams.append('tag', params.tag)
      }
      if (params.search) {
        queryParams.append('search', params.search)
      }

      const queryString = `?${queryParams.toString()}`
      
      const response = await this.requestConversations<ConversationStatsResponse>(queryString)
      return {
        success: response.success,
        conversations: response.conversations,
        error: undefined
      }
    } catch (error) {
      console.error('Error fetching conversations with pagination:', error)
      
      console.warn('New conversations endpoint not available, falling back to legacy clients endpoint')
      const fallbackResponse = await this.getAllClientsWithTags()
      
      if (fallbackResponse.success) {
        const allConversations: ConversationSummary[] = fallbackResponse.clients.map((client: any) => ({
          whatsappPhone: client.whatsappPhone,
          name: client.name,
          lastname: client.lastname,
          tag: client.tag || 'no_tag',
          messageCount: 0,
          toolsUsed: 0,
          tripsConsulted: 0,
          lastMessageAt: new Date(client.createdAt),
          chatId: 0
        }))

        const startIndex = (params.page - 1) * params.limit
        const endIndex = startIndex + params.limit
        const paginatedConversations = allConversations.slice(startIndex, endIndex)
        
        return {
          success: true,
          conversations: paginatedConversations,
          error: undefined
        }
      }
      
      throw error
    }
  }

  async getConversationsLegacy(filters?: ConversationFilters): Promise<ConversationsListResponse> {
    try {
      const queryParams = new URLSearchParams()
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            queryParams.append(key, String(value))
          }
        })
      }
      const params = queryParams.toString() ? `?${queryParams.toString()}` : ''
      
      const response = await this.requestConversations<ConversationStatsResponse>(params)
      return {
        success: response.success,
        conversations: response.conversations,
        error: undefined
      }
    } catch (error) {
      console.error('Error fetching conversations with stats:', error)
      
      console.warn('New conversations endpoint not available, falling back to legacy clients endpoint')
      const fallbackResponse = await this.getAllClientsWithTags()
      
      if (fallbackResponse.success) {
        const conversations: ConversationSummary[] = fallbackResponse.clients.map((client: any) => ({
          whatsappPhone: client.whatsappPhone,
          name: client.name,
          lastname: client.lastname,
          tag: client.tag || 'no_tag',
          messageCount: 0,
          toolsUsed: 0,
          tripsConsulted: 0,
          lastMessageAt: new Date(client.createdAt),
          chatId: 0
        }))
        
        return {
          success: true,
          conversations,
          error: undefined
        }
      }
      
      throw error
    }
  }
  
  async getConversationTokenStats(phone: string): Promise<{ success: boolean; stats: ConversationTokenStats }> {
    try {
      return await this.requestStats(`/conversation/${encodeURIComponent(phone)}/tokens`)
    } catch (error) {
      console.warn('Token stats endpoint not available, falling back to tag info')
      const tagInfo = await this.getCurrentClientTag(phone)
      return {
        success: true,
        stats: {
          totalTokens: 0,
          inputTokens: 0,
          outputTokens: 0,
          totalCost: 0,
          messageCount: 0,
          tag: tagInfo.tag
        }
      }
    }
  }

  async getConversationDetails(phone: string): Promise<ConversationDetailsResponse> {
    return this.requestConversations<ConversationDetailsResponse>(`/full/${encodeURIComponent(phone)}`)
  }
}

export const adminApi = new AdminApiService()
export type { ConversationTokenStats, ConversationStatsResponse, TagStatsResponse }